import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  SimpleChanges,
  TemplateRef,
  inject,
  input,
} from '@angular/core';

import { MomentModule } from 'ngx-moment';

import { GroupedOpenQuestionAnswer } from '@core/candidate-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { OfferStatusIndicatorComponent } from '@core/offers-ui';
import { TimeAgoPipe } from '@core/time-legacy';
import {
  AvatarComponent,
  CollapsibleCardComponent,
  HeadingComponent,
  RtTooltip,
  SegmentedButtonType,
  TruncateComponent,
} from '@core/user-interface-legacy';

import { CollapsibleCardAnalyticsDirective } from '../collapsible-card/collapsible-card-analytics.directive';
import { CandidateQuestionsResultComponent } from './candidate-questions-results/candidate-questions-result.component';
import type { CandidateQuestionsModuleTranslations } from './translations';
import { CandidateQuestionsTranslations } from './translations';
import { QuestionsMode } from './types';

@Component({
  selector: 'rt-candidate-questions',
  templateUrl: './candidate-questions.component.html',
  styleUrls: ['./candidate-questions.component.less'],
  host: {
    '[class.is-empty]':
      '!this.isReferralQuestionsExist && !this.isQuestionnairesExist && !this.isGroupedScreeningQuestionsExist',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HeadingComponent,
    CandidateQuestionsResultComponent,
    TranslatePipe,
    CollapsibleCardComponent,
    CollapsibleCardAnalyticsDirective,
    TruncateComponent,
    MomentModule,
    AvatarComponent,
    OfferStatusIndicatorComponent,
    TimeAgoPipe,
    RtTooltip,
  ],
})
export class CandidateQuestionsComponent {
  private readonly i18n = inject(I18nService);
  public readonly translations = inject<CandidateQuestionsModuleTranslations>(
    CandidateQuestionsTranslations,
  );

  public readonly QuestionsMode: typeof QuestionsMode = QuestionsMode;

  public readonly visibilityTplRef = input<TemplateRef<any>>(undefined!);
  public readonly questionnaire = input<GroupedOpenQuestionAnswer>();
  public readonly referralQuestions = input<GroupedOpenQuestionAnswer>();
  public readonly groupedScreeningQuestions = input<GroupedOpenQuestionAnswer>();
  public readonly canManageVisibility = input<boolean>(false);
  public readonly canManageCandidates = input<boolean>(false);
  public readonly canViewQuestionnaires = input<boolean>(false);
  public readonly canViewOpenQuestions = input<boolean>(false);
  public readonly referralsEnabled = input<boolean>(false);
  public readonly shouldShowOfferTitle = input<boolean, unknown>(true, {
    transform: booleanAttribute,
  });

  public segmentedButtons: Array<SegmentedButtonType<QuestionsMode>> = [];

  public title: string | undefined;

  public mode: QuestionsMode;

  public get isGroupedScreeningQuestionsExist(): boolean {
    const groupedScreeningQuestions = this.groupedScreeningQuestions();
    return (
      !!groupedScreeningQuestions?.offer &&
      groupedScreeningQuestions.openQuestionAnswers.length > 0 &&
      this.canViewOpenQuestions()
    );
  }

  public get isReferralQuestionsExist(): boolean {
    return !!this.referralQuestions() && this.referralsEnabled();
  }

  public get isQuestionnairesExist(): boolean {
    return !!this.questionnaire() && this.canViewQuestionnaires();
  }

  private get defaultMode(): QuestionsMode | undefined {
    if (this.isGroupedScreeningQuestionsExist) {
      return QuestionsMode.GroupedScreeningQuestions;
    } else if (this.isReferralQuestionsExist) {
      return QuestionsMode.ReferralQuestions;
    } else if (this.isQuestionnairesExist) {
      return QuestionsMode.Questionnaires;
    }

    return;
  }

  public ngOnInit() {
    this.setSegmentButtons();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.mode = this.mode || this.defaultMode!;
    this.resolveTitle();

    if ('questionnaires' in changes || 'referralsQuestions' in changes) {
      this.setSegmentButtons();
    }
  }

  public setSegmentButtons(): void {
    this.segmentedButtons = [
      {
        isActive: this.mode === QuestionsMode.GroupedScreeningQuestions,
        value: QuestionsMode.GroupedScreeningQuestions,
        label: this.i18n.translate(this.translations.titles.questions),
        isAvailable: this.isGroupedScreeningQuestionsExist,
      },
      {
        isActive: this.mode === QuestionsMode.ReferralQuestions,
        value: QuestionsMode.ReferralQuestions,
        label: this.i18n.translate(<string>this.translations.titles.referralQuestions),
        isAvailable: this.isReferralQuestionsExist,
      },
      {
        isActive: this.mode === QuestionsMode.Questionnaires,
        value: QuestionsMode.Questionnaires,
        label: this.i18n.translate(this.translations.titles.questionnaires),
        isAvailable: this.isQuestionnairesExist,
      },
    ];
  }

  private resolveTitle(): void {
    const i18nKeys: Record<QuestionsMode, string | undefined> = {
      [QuestionsMode.Questionnaires]: this.translations.titles.questionnaires,
      [QuestionsMode.ReferralQuestions]: this.translations.titles.referralQuestions,
      [QuestionsMode.GroupedScreeningQuestions]: this.translations.titles.questions,
    };

    this.title = i18nKeys[this.mode];
  }
}
